import { styled } from "@mui/material/styles";
import { PickersDay, StaticDatePicker } from "@mui/x-date-pickers";
import startOfDay from "date-fns/startOfDay";
import * as React from "react";
import { useCallback } from "react";

const CustomPickersDay = styled(PickersDay, {
  shouldForwardProp: (prop) => prop !== "selected",
})(({ theme, selected }) => ({
  ...(selected && {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    "&:hover, &:focus": {
      backgroundColor: theme.palette.primary.dark,
    },
    borderTopLeftRadius: "50%",
    borderBottomLeftRadius: "50%",
    borderTopRightRadius: "50%",
    borderBottomRightRadius: "50%",
  }),
}));

interface MultiDatePickerProps {
  dates: Date[];
  onChange: (dates: Date[]) => void;
  label?: string;
  disablePast?: boolean;
}

export default function MultiDatePicker({
  dates,
  onChange,
  label,
  disablePast,
}: MultiDatePickerProps) {
  // Maintain a separate state for the picker’s “active” date.
  const [activeDate, setActiveDate] = React.useState<Date>(new Date());

  const findDate = (dates: Date[], date: Date) => {
    const dateTime = date.getTime();
    return dates.find((item) => item.getTime() === dateTime);
  };

  const findIndexDate = (dates: Date[], date: Date) => {
    const dateTime = date.getTime();
    return dates.findIndex((item) => item.getTime() === dateTime);
  };

  const handleDateToggle = useCallback(
    (newValue: Date) => {
      const array = [...dates];

      const date = startOfDay(newValue);
      const index = findIndexDate(array, date);
      if (index >= 0) {
        array.splice(index, 1);
      } else {
        array.push(date);
      }
      setActiveDate(newValue);
      onChange(array);
    },
    [dates, onChange],
  );

  const renderPickerDay = (
    date: Date,
    selectedDates: Date[],
    pickersDayProps: any,
  ) => {
    const selected = findDate(dates, date);
    return (
      <CustomPickersDay
        {...pickersDayProps}
        selected={!!selected}
        onClick={(event) => {
          // Call our toggle logic regardless of whether the day is the same.
          handleDateToggle(date);
          // Also call the original onClick if needed.
          if (pickersDayProps.onClick) {
            pickersDayProps.onClick(event);
          }
        }}
      />
    );
  };

  return (
    <StaticDatePicker
      displayStaticWrapperAs="desktop"
      sx={{
        justifyContent: "start",
      }}
      value={activeDate} // now a single date, not an array
      disablePast={disablePast}
      slots={{
        day: (props) => renderPickerDay(props.day, dates, props),
      }}
    />
  );
}
